import { useImperativeHandle, useRef, useState, useCallback, useMemo, memo, forwardRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal } from "@mui/material";
import AppButton from "components/app-button";
import PropTypes from "prop-types";
import serveRequestErrors from "common/serve-request-errors";
import sanitizeObject from "common/sanitize-object";
import api from "services/api";

export const AppEmployeesListModal = (props, ref) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const paramsRef = useRef({
        sort: searchParams.get("sort") || "",
    });
    const [allEmployees, setAllEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const onHandleGetList = useCallback(async () => {
        setLoading(true);

        try {
            const payload = { ...paramsRef.current };
            memoSetSearchParams.current(sanitizeObject(payload));

            const response = await api.get.admin.employeeAllList(sanitizeObject(payload));

            if (response) {
                // Debug: Log the first employee to see the data structure
                if (response.length > 0) {
                    console.log('Employee data structure:', response[0]);
                }
                setAllEmployees(response);
            }
        } catch (error) {
            serveRequestErrors(error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Filter available employees (not already selected)
    const availableEmployees = useMemo(() => {
        const selectedIds = new Set(selectedEmployees.map(emp => emp.id));
        return allEmployees.filter(emp => !selectedIds.has(emp.id));
    }, [allEmployees, selectedEmployees]);

    // Filter employees based on search term
    const filteredAvailableEmployees = useMemo(() => {
        if (!searchTerm.trim()) return availableEmployees;
        
        const term = searchTerm.toLowerCase();
        return availableEmployees.filter(emp => 
            emp.name?.toLowerCase().includes(term) ||
            emp.email?.toLowerCase().includes(term) ||
            emp.employeeId?.toString().includes(term)
        );
    }, [availableEmployees, searchTerm]);

    const onHandleSelectEmployee = useCallback((employee) => {
        setSelectedEmployees(prev => [...prev, employee]);
    }, []);

    const onHandleRemoveEmployee = useCallback((employeeId) => {
        setSelectedEmployees(prev => prev.filter(emp => emp.id !== employeeId));
    }, []);

    const onHandleConfirm = () => {
        props.onHandleSetEmployees(selectedEmployees);
        setVisible(false);
        setSearchTerm("");
    };

    const onHandleDismiss = () => {
        // Reset to the previously confirmed selection
        setSelectedEmployees(props.currentSelectedEmployees || []);
        setVisible(false);
        setSearchTerm("");
    };

    const onHandleShow = async () => {
        // Initialize with current selection from parent
        setSelectedEmployees(props.currentSelectedEmployees || []);
        setVisible(true);
        await onHandleGetList();
    };

    useImperativeHandle(ref, () => ({
        onHandleShow: () => onHandleShow(),
    }));

    return (
        <Modal 
            classes={{ root: "app-employees-list-modal" }} 
            open={visible} 
            onClose={onHandleDismiss} 
            aria-labelledby="employees-list-modal" 
            aria-describedby="employees-list-modal"
        >
            <div className="employees-list-modal">
                <div className="employees-list-modal__main">
                    <div className="employees-list-modal__header">
                        <p className="employees-list-modal__title">Choose Employees</p>
                    </div>

                    <div className="employees-list-modal__body">
                        {loading ? (
                            <div className="employees-list-modal__loading">
                                <span className="employees-list-modal__loading--spinner" />
                                <span className="employees-list-modal__loading--text">Loading…</span>
                            </div>
                        ) : (
                            <div className="employees-list-modal__content">
                                {/* Available Employees Section */}
                                <div className="employees-list-modal__section">
                                    <div className="employees-list-modal__section-header">
                                        <h3>Available Employees ({filteredAvailableEmployees.length})</h3>
                                        <input
                                            type="text"
                                            className="employees-list-modal__search"
                                            placeholder="Search by name, email, or ID..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className="employees-list-modal__list">
                                        {filteredAvailableEmployees.length === 0 ? (
                                            <p className="employees-list-modal__empty">
                                                {searchTerm ? "No employees match your search" : "No available employees"}
                                            </p>
                                        ) : (
                                            filteredAvailableEmployees.map((employee) => (
                                                <div 
                                                    key={employee.id} 
                                                    className="employees-list-modal__item"
                                                >
                                                    <div className="employees-list-modal__item-info">
                                                        <p className="employees-list-modal__item-name">
                                                            {employee.name || 'N/A'}
                                                        </p>
                                                        <p className="employees-list-modal__item-email">
                                                            {employee.email || 'N/A'}
                                                        </p>
                                                        <p className="employees-list-modal__item-id">
                                                            ID: {employee.employeeId || employee.id || 'N/A'}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="employees-list-modal__item-button"
                                                        onClick={() => onHandleSelectEmployee(employee)}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Selected Employees Section */}
                                <div className="employees-list-modal__section">
                                    <div className="employees-list-modal__section-header">
                                        <h3>Selected Employees ({selectedEmployees.length})</h3>
                                    </div>
                                    <div className="employees-list-modal__list">
                                        {selectedEmployees.length === 0 ? (
                                            <p className="employees-list-modal__empty">
                                                No employees selected
                                            </p>
                                        ) : (
                                            selectedEmployees.map((employee) => (
                                                <div 
                                                    key={employee.id} 
                                                    className="employees-list-modal__item employees-list-modal__item--selected"
                                                >
                                                    <div className="employees-list-modal__item-info">
                                                        <p className="employees-list-modal__item-name">
                                                            {employee.name || 'N/A'}
                                                        </p>
                                                        <p className="employees-list-modal__item-email">
                                                            {employee.email || 'N/A'}
                                                        </p>
                                                        <p className="employees-list-modal__item-id">
                                                            ID: {employee.employeeId || employee.id || 'N/A'}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="employees-list-modal__item-button employees-list-modal__item-button--remove"
                                                        onClick={() => onHandleRemoveEmployee(employee.id)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="employees-list-modal__button-container">
                            <AppButton 
                                type="button" 
                                label="Cancel" 
                                outline 
                                onClick={onHandleDismiss} 
                            />
                            <AppButton 
                                type="submit" 
                                label="Confirm" 
                                onClick={onHandleConfirm}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default memo(forwardRef(AppEmployeesListModal));

AppEmployeesListModal.propTypes = {
    ref: PropTypes.object,
    onHandleSetEmployees: PropTypes.func.isRequired,
    currentSelectedEmployees: PropTypes.array,
};